import { useEffect, useState } from "react";
import { LockKeyhole, MessageSquarePlus } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Select, Textarea } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import type { Campaign } from "@/shared/types";

export function CommunicationsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [error, setError] = useState("");
  const load = () => api<Campaign[]>("/admin/campaigns").then(setCampaigns);
  useEffect(() => {
    void load();
  }, []);
  const create = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    await api("/admin/campaigns", {
      method: "POST",
      body: JSON.stringify({
        name: data.get("name"),
        location_id: "mountain-view",
        channel: data.get("channel"),
        content: {
          "en-US": data.get("content_en"),
          "es-US": data.get("content_es"),
        },
        audience: { relationships: [data.get("relationship")] },
      }),
    });
    event.currentTarget.reset();
    await load();
  };
  const send = async (campaign: Campaign) => {
    setError("");
    try {
      await api(`/admin/campaigns/${campaign.id}/send`, { method: "POST" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to send.");
    }
  };
  return (
    <div className="mx-auto max-w-7xl">
      <h1 className="text-3xl font-bold tracking-tight">Communications</h1>
      <p className="mt-2 text-muted-foreground">
        One localized message per neighbor, with consent-aware audience counts.
      </p>
      <Card className="mt-7 border-amber-200 bg-amber-50">
        <CardContent className="flex gap-3 p-5">
          <LockKeyhole className="mt-0.5 shrink-0 text-amber-700" size={20} />
          <div>
            <strong>Outbound delivery is disabled</strong>
            <p className="mt-1 text-sm text-amber-900/70">
              Drafts and recipient counts are safe to test. Real sends require
              approved Twilio/Mailgun senders, message wording, allowlists, and
              opt-out handling.
            </p>
          </div>
        </CardContent>
      </Card>
      <div className="mt-6 grid gap-6 lg:grid-cols-[420px_1fr]">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquarePlus size={20} />
              New draft
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={create}>
              <div>
                <Label className="mb-2 block">Campaign name</Label>
                <Input name="name" required />
              </div>
              <div>
                <Label className="mb-2 block">Channel</Label>
                <Select name="channel">
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">Audience</Label>
                <Select name="relationship">
                  <option value="neighbor">Neighbors</option>
                  <option value="volunteer">Volunteers</option>
                  <option value="supporter">Supporters</option>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">English</Label>
                <Textarea name="content_en" required maxLength={1500} />
              </div>
              <div>
                <Label className="mb-2 block">Spanish</Label>
                <Textarea name="content_es" maxLength={1500} />
              </div>
              <Button type="submit">Save draft</Button>
            </form>
          </CardContent>
        </Card>
        <div className="space-y-4">
          {error && (
            <p className="rounded-lg bg-red-50 p-4 text-sm text-red-800">
              {error}
            </p>
          )}
          {campaigns.map((campaign) => (
            <Card key={campaign.id}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex gap-2">
                      <Badge>{campaign.channel.toUpperCase()}</Badge>
                      <Badge>{campaign.state}</Badge>
                    </div>
                    <h2 className="mt-3 font-bold">{campaign.name}</h2>
                    <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                      {campaign.content["en-US"]}
                    </p>
                  </div>
                  <div className="text-right">
                    <strong className="text-2xl">
                      {campaign.recipient_count}
                    </strong>
                    <p className="text-xs text-muted-foreground">
                      eligible recipients
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="mt-5"
                  disabled={!campaign.send_enabled}
                  onClick={() => void send(campaign)}
                >
                  Send campaign
                </Button>
              </CardContent>
            </Card>
          ))}
          {!campaigns.length && (
            <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              No campaign drafts yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
